import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy, inject, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Subscription, firstValueFrom, from, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
    readonly required = signal(false);
    readonly updating = signal(false);
    readonly error = signal<string | null>(null);

    private readonly document = inject(DOCUMENT);
    private readonly zone = inject(NgZone);
    private readonly sw = inject(SwUpdate);
    // La comprobacion debe funcionar antes del login, sin spinner ni caches del SW.
    private readonly http = new HttpClient(inject(HttpBackend));
    private readonly subscriptions = new Subscription();
    private readonly currentFrontend = this.document.querySelector<HTMLMetaElement>('meta[name="app-build-version"]')?.content;
    private backendVersion?: string;
    private started = false;
    private checking = false;
    private interval?: ReturnType<typeof setInterval>;
    private readonly onResume = () => {
        if (!this.document.hidden) void this.check();
    };

    start(): void {
        if (this.started) return;
        this.started = true;
        if (this.sw.isEnabled) {
            this.subscriptions.add(this.sw.versionUpdates.subscribe(event => {
                if (event.type === 'VERSION_READY') this.required.set(true);
                if (event.type === 'VERSION_INSTALLATION_FAILED') console.error('No se pudo instalar la nueva version', event.error);
            }));
            this.subscriptions.add(this.sw.unrecoverable.subscribe(event => {
                console.error('La version de la aplicacion no puede recuperarse', event.reason);
                this.required.set(true);
            }));
        }
        this.document.addEventListener('visibilitychange', this.onResume);
        this.document.defaultView?.addEventListener('online', this.onResume);
        this.document.defaultView?.addEventListener('focus', this.onResume);
        this.zone.runOutsideAngular(() => {
            this.interval = setInterval(() => void this.check(), 60_000);
        });
        void this.check();
    }

    async check(): Promise<void> {
        if (this.checking || this.updating()) return;
        this.checking = true;
        try {
            await Promise.all([
                this.checkFrontend(),
                this.checkBackend(),
                this.checkWorker(),
            ]);
        } finally {
            this.checking = false;
        }
    }

    private async versionAt(url: string): Promise<string> {
        const result = await firstValueFrom(this.http.get<unknown>(url, {
            params: { 'ngsw-bypass': 'true', _: String(Date.now()) },
            headers: { 'Cache-Control': 'no-cache' }
        }).pipe(timeout(15_000)));
        if (!result || typeof result !== 'object' || !('version' in result)
            || typeof result.version !== 'string' || !/^[a-f0-9]{64}$/.test(result.version)) {
            throw new Error('Respuesta de version invalida');
        }
        return result.version;
    }

    private frontendUrl(): string {
        return new URL('version.json', this.document.baseURI).href;
    }

    private backendUrl(): string {
        return `${environment.url.replace(/\/?$/, '/')}version`;
    }

    private async checkFrontend(): Promise<void> {
        // ng serve no genera un artefacto desplegado ni debe forzar actualizaciones.
        if (!this.currentFrontend) return;
        try {
            const latest = await this.versionAt(this.frontendUrl());
            if (latest !== this.currentFrontend) this.zone.run(() => this.required.set(true));
        } catch (error) {
            console.warn('No se pudo comprobar la version del frontend; se reintentara', error);
        }
    }

    private async checkBackend(): Promise<void> {
        try {
            const latest = await this.versionAt(this.backendUrl());
            if (this.backendVersion && latest !== this.backendVersion) {
                this.zone.run(() => this.required.set(true));
            }
            this.backendVersion = latest;
        } catch (error) {
            console.warn('No se pudo comprobar la version del backend; se reintentara', error);
        }
    }

    private async checkWorker(): Promise<void> {
        if (!this.sw.isEnabled) return;
        try {
            await firstValueFrom(from(this.sw.checkForUpdate()).pipe(timeout(30_000)));
        } catch (error) {
            console.warn('No se pudo comprobar la actualizacion PWA; se reintentara', error);
        }
    }

    async update(): Promise<void> {
        if (this.updating() || !this.required()) return;
        this.updating.set(true);
        this.error.set(null);
        try {
            // No recargar hacia una pagina incompleta durante un corte o despliegue.
            await Promise.all([
                ...(this.currentFrontend ? [this.versionAt(this.frontendUrl())] : []),
                this.versionAt(this.backendUrl()),
            ]);
            if (this.sw.isEnabled) {
                await firstValueFrom(from(this.sw.checkForUpdate()).pipe(timeout(30_000)));
                await firstValueFrom(from(this.sw.activateUpdate()).pipe(timeout(30_000)));
            }
            this.reloadPage();
        } catch (error) {
            console.error('No se pudo actualizar el sistema', error);
            this.error.set('No se pudo completar la actualización. Verificá tu conexión y presioná Actualizar nuevamente.');
        } finally {
            this.updating.set(false);
        }
    }

    reloadPage(): void {
        const browser = this.document.defaultView;
        if (!browser) throw new Error('No hay una ventana disponible para actualizar');
        const target = new URL(browser.location.href);
        target.searchParams.set('_appUpdate', String(Date.now()));
        target.searchParams.set('ngsw-bypass', 'true');
        browser.location.replace(target.href);
    }

    ngOnDestroy(): void {
        if (this.interval) clearInterval(this.interval);
        this.subscriptions.unsubscribe();
        this.document.removeEventListener('visibilitychange', this.onResume);
        this.document.defaultView?.removeEventListener('online', this.onResume);
        this.document.defaultView?.removeEventListener('focus', this.onResume);
    }
}
